import React from 'react';
import { FormControl, FormGroup } from 'react-bootstrap';
import { CurrentUser, SignupFormValidatorGeneric } from 'Roblox';
import { ensureSignupFormValidatorResources } from '../../../constants/signupFormValidatorResources';

/**
 * A helper function that captures [Enter] presses within input elements
 * (especially those that are not bound to forms with submit behavior).
 */
const handleEnter = (handler: () => void | Promise<void>) => async (
  event: React.KeyboardEvent<unknown>
): Promise<void> => {
  // Enter key.
  if (event.keyCode === 13) {
    await handler();
  }
};

type InputValidator = (value: string) => string | null;

/**
 * Returns null (no error) for every argument.
 */
export const validateTrue: InputValidator = () => null;

/**
 * Returns null (no error) if the argument is an acceptable password (meets
 * certain requirements and does not match the user's username).
 */
export const validatePassword: InputValidator = (value: string) => {
  ensureSignupFormValidatorResources();
  const username = CurrentUser?.name || '';
  const errorMessage = SignupFormValidatorGeneric.getInvalidPasswordMessage(value, username);
  if (errorMessage === '') {
    return null;
  }

  return errorMessage;
};

/**
 * Returns null (no error) if the argument looks like an email address.
 */
export const validateEmailAddress = (errorMessage: string): InputValidator => (value: string) => {
  const re = /\S+@\S+\.\S+/;
  if (re.test(value)) {
    return null;
  }

  return errorMessage;
};

/**
 * Returns null (no error) if the argument equals some target value.
 */
export const validateEquals = (errorMessage: string, target: string): InputValidator => (
  value: string
) => {
  if (value === target) {
    return null;
  }

  return errorMessage;
};

/**
 * Returns null (no error) if all the passed input validators return null for
 * the argument (after being applied in sequence).
 */
export const validateChained = (...inputValidators: InputValidator[]): InputValidator => (
  value: string
) => {
  for (let i = 0; i < inputValidators.length; i += 1) {
    const errorMessage = inputValidators[i](value);
    if (errorMessage !== null) {
      return errorMessage;
    }
  }

  return null;
};

/**
 * A helper function that captures form input `onChange` events, validating the
 * input value and setting the appropriate React state variables.
 */
const validateAndSetStateForInput = (
  validate: InputValidator,
  setValue: (value: string) => void,
  setError: (errorMessage: string | null) => void,
  onChangeCustom?: (value: string) => void
) => (event: React.FormEvent<FormControl & HTMLInputElement>): void => {
  const { value } = event.currentTarget;
  if (onChangeCustom !== undefined) {
    onChangeCustom(value);
  }
  setValue(value);
  setError(validate(value));
};

type Props = {
  id: string;
  label: string;
  inputType: string;
  placeholder: string;
  disabled: boolean;
  value: string;
  setValue: (value: string) => void;
  error: string | null;
  setError: (errorMessage: string | null) => void;
  validate: InputValidator;
  handleSubmit: () => void | Promise<void>;
  /** Additional optional code to run on input change. */
  onChange: () => void;
};

/**
 * An input control that encapsulates Roblox-styled inputs with labels and
 * error validation.
 *
 * TODO: Allow password inputs to be toggled for visibility.
 */
const InputControl: React.FC<Props> = ({
  id,
  label,
  inputType,
  placeholder,
  disabled,
  value,
  error,
  validate,
  setValue,
  setError,
  handleSubmit,
  onChange: onChangeCustom
}: Props) => {
  const valueHasSuccess = value !== '' && error === null;
  const valueHasError = value !== '' && error !== null;
  const valueHasFeedback = valueHasSuccess || valueHasError;

  return (
    <div>
      <label className='text-label xsmall' htmlFor={id}>
        {label}
      </label>
      <FormGroup
        controlId={id}
        className={`${valueHasFeedback ? 'form-has-feedback' : ''} ${
          valueHasSuccess ? 'form-has-success' : ''
        } ${valueHasError ? 'form-has-error' : ''}`}>
        <FormControl
          as='input'
          className='input-field'
          type={inputType}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={validateAndSetStateForInput(validate, setValue, setError, onChangeCustom)}
          onKeyDown={valueHasSuccess ? handleEnter(handleSubmit) : undefined}
        />
        {!disabled && valueHasSuccess && <span className='icon-checkmark-on' />}
        {!disabled && valueHasError && <span className='icon-close' />}
        {/* Prevent reflow of elements when we get an error. */}
        <p className='form-control-label xsmall'>{!disabled && valueHasError ? error : '\u00A0'}</p>
      </FormGroup>
    </div>
  );
};

export default InputControl;
